import { type FC, useCallback, useMemo, useRef } from 'react';
import { useObjectTableColumns } from 'components/ObjectTable/ObjectTable';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useSortColumnsModal } from 'hooks/useSortColumnsModal';
import { getText } from 'i18n/intl';
import { mapValues } from 'lodash';
import { TFindObjectsPayload, findMoreObjects } from 'slices/object.slice';
import { isBulkEditJob } from 'types/objectJob.guards';
import { TObjectJob } from 'types/objectJob.type';
import type { TTEObject } from '@timeedit/types';
import {
  EditableTable,
  type GetMore,
  Typography,
  useInfiniteScroll,
} from '@timeedit/ui-components';
import { Button, Modal } from '@timeedit/ui-components/antd';
import BulkEditStartJobButton, {
  useJobBulkEditProps,
} from '../../../BulkEditStartJobButton';
import './JobObjectsModal.scss';

const { Text } = Typography;

type Props = {
  open: boolean;
  job: TObjectJob;
  onClose: () => void;
  objectExtIds: string[];
  objectTypeId: number;
  objectTypeName: string;
};

const LIMIT_PER_PAGE = 50;
const ITEM_HEIGHT = 36;

const JobObjectsModal: FC<Props> = (props) => {
  const bulkEditProps = useJobBulkEditProps(props.job);
  const { jobSummary } = bulkEditProps;

  const showStartNewOperationButton = isBulkEditJob(props.job);

  const footerContent: React.ReactNode[] = useMemo(() => {
    const content: React.ReactNode[] = [];
    if (showStartNewOperationButton) {
      content.push(
        <BulkEditStartJobButton
          key="start-new-operation"
          {...bulkEditProps}
          type="default"
          size="middle"
        >
          Start new operation ({jobSummary.total} objects)
        </BulkEditStartJobButton>
      );
    }
    content.push(
      <Button type="primary" size="middle" key="ok" onClick={props.onClose}>
        Ok
      </Button>
    );
    return content;
  }, [showStartNewOperationButton, bulkEditProps, jobSummary, props.onClose]);

  return (
    <Modal
      destroyOnHidden
      onOk={props.onClose}
      onCancel={props.onClose}
      closable={false}
      open={props.open}
      width={'75vw'}
      footer={footerContent}
    >
      <div className="job-objects-modal__title">
        <Text>
          <Text strong>Object type:</Text> {props.objectTypeName}{' '}
          <Text strong>Objects:</Text> {props.objectExtIds.length}
        </Text>
      </div>
      <ObjectTable
        objectExtIds={props.objectExtIds}
        objectTypeId={props.objectTypeId}
      />
    </Modal>
  );
};

type ObjectTableProps = {
  objectExtIds: string[];
  objectTypeId: number;
};

const ObjectTable: FC<ObjectTableProps> = (props) => {
  const doneInitialFetch = useRef(false);
  const { getMore } = usePagingByObjectId(props.objectExtIds);

  const [ref, defaultTableProps, height] = useDefaultTableProps(0);

  const {
    data: objects = [],
    onScroll,
    loading,
  } = useInfiniteScroll<TTEObject>({
    className: '.rc-virtual-list-holder-inner',
    getMore,
    itemHeight: ITEM_HEIGHT,
    containerHeight: height,
    limit: LIMIT_PER_PAGE,
    virtual: true,
    initialFetch: props.objectExtIds.length > 0 && !doneInitialFetch.current,
  });

  doneInitialFetch.current = true;

  const columns = useObjectTableColumns(props.objectTypeId);

  const updatedColumns = useMemo(() => {
    const unsortableObjects = mapValues(columns, (col) => ({
      ...col,
      sorter: undefined,
      ellipsis: true,
    }));

    unsortableObjects.active.ellipsis = false;

    return unsortableObjects;
  }, [columns]);

  const { sortColumnsModal, openSortColumnsModal, sortedColumns } =
    useSortColumnsModal(updatedColumns);

  const title = useCallback(() => {
    return (
      <div className="job-objects-modal__table-title">
        <Button onClick={openSortColumnsModal} size="small">
          {getText('ui.table.edit_columns')}
        </Button>
      </div>
    );
  }, [openSortColumnsModal]);

  return (
    <div ref={ref} className="job-objects-modal__table-container">
      <EditableTable
        {...defaultTableProps}
        loading={loading}
        dataSource={objects}
        title={title}
        columns={sortedColumns as any}
        virtual={true}
        fitColumns={false}
        onScroll={onScroll}
      />
      {sortColumnsModal}
    </div>
  );
};

const usePagingByObjectId = (objectExtIds: string[]) => {
  const getMore = useCallback<GetMore<TTEObject>>(
    async (options) => {
      const pageSize = options?.limit ?? LIMIT_PER_PAGE;
      const pageNo = options?.page ?? 1;
      const offset = (pageNo - 1) * pageSize;

      const idSlice = objectExtIds.slice(offset, offset + pageSize);

      const { results } = await findMoreObjects({
        extIds: idSlice,
      } satisfies TFindObjectsPayload);

      return {
        data: results,
        page: pageNo,
        count: results.length,
        total: objectExtIds.length,
        hasMore: objectExtIds.length > offset + pageSize,
      };
    },
    [objectExtIds]
  );

  return useMemo(() => ({ getMore }), [getMore]);
};

export default JobObjectsModal;
